import { Button, InlineMessage, PageContent, Stack } from '@olytic/foundation';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { authenticatedFetch } from '../auth/authClient';
import { useDocumentTitle } from '../documentTitle';
import { readCase } from './businessCase';
import { ProposalDocument } from './ProposalDocument';
import { readLayout } from './proposal';
import { fetchBuilder, type QuoteBuilder } from './quotesApi';
import { deriveWorkspace } from '@quote-math/workspace';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * The proposal on its own page, to print or save as PDF: the same document
 * as the Proposal tab. `?print=1` opens the print dialog once it has drawn.
 */
export function ProposalPage(): JSX.Element {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState<QuoteBuilder | null>(null);
  const [error, setError] = useState<string | null>(null);
  useDocumentTitle(data ? `Proposal: ${data.quote.name ?? data.quote.quote_number}` : 'Proposal');

  useEffect(() => {
    let live = true;
    fetchBuilder(authenticatedFetch, id)
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load the quote.'));
    return () => {
      live = false;
    };
  }, [id]);

  const autoPrint = params.get('print') === '1';
  useEffect(() => {
    if (!data || !autoPrint) return;
    // Give the charts a moment to draw.
    const timer = window.setTimeout(() => window.print(), 800);
    return () => window.clearTimeout(timer);
  }, [data, autoPrint]);

  if (error) return <PageContent><InlineMessage tone="error" role="alert">{error}</InlineMessage></PageContent>;
  if (!data) return <PageContent aria-busy="true" />;

  const { quote } = data;
  const { timeline, terms, valueModel } = deriveWorkspace(data);
  return (
    <PageContent as="section">
      <Stack>
        <div className="flex justify-end gap-2 print:hidden">
          <Button asChild variant="neutral">
            <Link to={`/quotes/${quote.id}`}>Back to the quote</Link>
          </Button>
          <Button variant="brand" onClick={() => window.print()}>
            Print or save as PDF
          </Button>
        </div>
        <ProposalDocument
          data={data}
          layout={readLayout(quote.proposal)}
          memo={readCase(quote.proposal)}
          terms={terms}
          timeline={timeline}
          valueModel={valueModel}
          prepared={today()}
          bare
        />
      </Stack>
    </PageContent>
  );
}
